import { Progress } from '@heroui/react';
import { Svg } from '@/shared/ui/Svg';
import { formatFileSize } from '@/shared/utils';
import UploadIcon from '@/shared/assets/icons/files/upload-video.svg';
import DeleteFileIcon from '@/shared/assets/icons/files/delete-file.svg';

export interface ScormUploadState {
  progress: number;
  fileName: string;
  fileSize: number;
  error: string | null;
  state: 'idle' | 'preparing' | 'uploading' | 'completed' | 'error';
  statusText?: string;
}

interface ScormUploadingProps {
  uploadState: ScormUploadState;
  selectedFile: File | null;
  onCancel: () => void;
}

export const ScormUploading = ({
  uploadState,
  selectedFile,
  onCancel,
}: ScormUploadingProps) => (
  <div className="flex flex-col gap-4">
    <p className="text-lg font-medium">{uploadState.statusText || 'File ready to upload'}</p>
    <div className="border border-gray-200 rounded-lg pt-3 pb-2 px-4">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 bg-[#211D55] rounded-[8px] flex items-center justify-center flex-shrink-0">
          <Svg Icon={UploadIcon} width={24} height={24} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate text-[#293056] tracking-[-0.5%]">
            {uploadState.fileName}
          </p>
          <p className="text-xs font-medium text-[#9CA3AF] tracking-[-0.5%]">
            Format: ZIP
            <span className="ml-3">Size: {formatFileSize(uploadState.fileSize)}</span>
          </p>
        </div>
        <button
          onClick={onCancel}
          className="flex-shrink-0 w-9 h-8 flex items-center justify-center border border-gray-200 rounded-[8px] cursor-pointer"
          aria-label="Remove file"
          type="button"
        >
          <Svg Icon={DeleteFileIcon} width={24} height={24} className="text-grey-description" />
        </button>
      </div>
      {(uploadState.state === 'uploading' || uploadState.state === 'preparing') && (
        <>
          <Progress
            value={uploadState.progress}
            className="mt-[10px]"
            size="md"
            aria-label={`Upload progress: ${uploadState.progress}%`}
            classNames={{
              track: 'bg-[#ededed] h-[7px] rounded-[16px]',
              indicator: 'bg-[#211D55] rounded-[16px]',
            }}
          />
          <p className="text-xs text-[#9CA3AF] tracking-[-0.5%] text-right">
            {uploadState.progress}%
          </p>
        </>
      )}
    </div>
    {uploadState.error && (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600 text-sm">{uploadState.error}</p>
      </div>
    )}
  </div>
);
